// src/context/NodeContext.tsx (Fixed with Store Subscription)
import React, {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
} from "react";
import {
  NodeChange,
  EdgeChange,
  applyNodeChanges,
  applyEdgeChanges,
} from "@xyflow/react";
import { useModelNodeStore } from "@/stores/modelNodeStore";
import { ModelNodeInterface } from "@/interfaces/uiNodeInterfaces/ModelNodeInterface";
import { ModelInterface, ModelPropertyInterface, ModelPropertyType } from "@/interfaces";
import { calculateNewNodePositions } from "@/utils/flow/flowUtils";

interface NodeContextValue {
  modelNodes: ModelNodeInterface[];
  edges: any[];
  onNodesChange: (changes: NodeChange<ModelNodeInterface>[]) => void;
  onEdgesChange: (changes: EdgeChange[]) => void;
  addNode: () => ModelNodeInterface;
  removeNode: (id: string) => string;
  deleteModel: (
    modelId: string,
    confirm: (msg: string) => Promise<boolean>
  ) => Promise<void>;
  addEdge: (from: string, to: string, property?: string) => void;
  removeEdge: (id: string) => string;
  onConnect: (params: any) => void;
  bulkAddNodes: (
    models: ModelInterface[],
    baseNodesOverride?: ModelNodeInterface[]
  ) => ModelNodeInterface[];
  addAuthentication: () => void;
  addRoleModel: () => void;
  setNodesFromModels: (models: ModelInterface[]) => void;
  clearNodes: () => void;
  getModelsFromNodes: () => ModelInterface[];
  alignNodes: () => void;
}

const NodeContext = createContext<NodeContextValue | undefined>(undefined);

const DEFAULT_AUTH_PROPERTIES: ModelPropertyInterface[] = [
  { name: "name", type: ModelPropertyType.String, required: true },
  { name: "email", type: ModelPropertyType.String, required: true, unique: true },
  { name: "password", type: ModelPropertyType.String },
  { name: "googleId", type: ModelPropertyType.String },
  { name: "picture", type: ModelPropertyType.String },
];

const DEFAULT_ROLE_PROPERTIES: ModelPropertyInterface[] = [
  { name: "type", type: ModelPropertyType.String, required: true, unique: true },
  { name: "description", type: ModelPropertyType.String, required: false },
];

export const NodeContextProvider: React.FC<{
  children: React.ReactNode;
  onError: (title: string, message?: string) => void;
  onSuccess: (title: string, message?: string) => void;
}> = ({ children, onError, onSuccess }) => {
  // Subscribe to store updates - this makes modelNodes/edges reactive
  const modelNodes = useModelNodeStore((state) => state.modelNodes);
  const edges = useModelNodeStore((state) => state.edges);
  const store = useModelNodeStore();

  // ==================== REACT FLOW HANDLERS ====================

  const onNodesChange = useCallback(
    (changes: NodeChange<ModelNodeInterface>[]) => {
      const updatedModelNodes = applyNodeChanges(changes, modelNodes);

      // Update the store with new nodes array
      useModelNodeStore.setState((state) => {
        state.modelNodes = updatedModelNodes;

        // Also update the maps to keep them in sync
        updatedModelNodes.forEach((node) => {
          state.modelNodeById.set(node.id, node);
          state.modelNodeByName.set(node.data.name.toLowerCase(), node);
        });
      });
    },
    [modelNodes]
  );

  const onEdgesChange = useCallback(
    (changes: EdgeChange[]) => {
      const updatedEdges = applyEdgeChanges(changes, edges);
      useModelNodeStore.setState({ edges: updatedEdges });
    },
    [edges]
  );

  const onConnect = useCallback(
    (params: any) => {
      if (params.source && params.target) {
        store.addEdge(params.source, params.target, params.sourceHandle);
      }
    },
    [store]
  );

  // ==================== NODE OPERATIONS ====================

  const addNode = useCallback(() => {
    return store.addModelNode();
  }, [store]);

  const removeNode = useCallback(
    (id: string) => {
      store.removeModelNode(id);
      return id;
    },
    [store]
  );

  const deleteModel = useCallback(
    async (modelId: string, confirm: (msg: string) => Promise<boolean>) => {
      const nodeToDelete = store.findModelNodeById(modelId);
      if (!nodeToDelete) return;

      const referencingModelNodes = modelNodes.filter(
        (n) =>
          n.id !== modelId &&
          n.data?.properties?.some(
            (p) =>
              p.type === ModelPropertyType.Reference &&
              p.reference === nodeToDelete.data?.name
          )
      );

      const message =
        referencingModelNodes.length > 0
          ? `Removing model "${nodeToDelete.data?.name}" will remove references from ${referencingModelNodes.length} other model(s). Continue?`
          : `Remove model "${nodeToDelete.data?.name}"?`;

      const proceed = await confirm(message);
      if (!proceed) return;

      referencingModelNodes.forEach((node) => {
        const filteredProps = node.data.properties.filter(
          (p) =>
            !(
              p.type === ModelPropertyType.Reference &&
              p.reference === nodeToDelete.data?.name
            )
        );
        store.setModelNodeProperties(node.id, filteredProps);
      });

      store.removeModelNode(modelId);
    },
    [store, modelNodes]
  );

  // ==================== BULK OPERATIONS ====================



  // ... existing imports

  // ... inside NodeContextProvider

  // ==================== BULK OPERATIONS ====================

  const bulkAddNodes = useCallback(
    (models: ModelInterface[], baseNodesOverride?: ModelNodeInterface[]) => {
      if (baseNodesOverride !== undefined) {
        store.clearModelNodes();
      }

      // 1. Deep copy and filter references
      const processedModels = models.map((model) => ({
        ...model,
        properties: model.properties.filter(
          (p) => p.type !== ModelPropertyType.Reference
        ),
      }));

      // 2. Calculate positions
      const existingNodes = store.modelNodes;
      const newPositions = calculateNewNodePositions(processedModels, existingNodes);

      // 3. Assign positions
      processedModels.forEach((model) => {
        if (newPositions[model.name]) {
          model.position = newPositions[model.name];
        }
      });

      return store.bulkAddModelNodes(processedModels);
    },
    [store]
  );

  // ==================== AUTHENTICATION ====================

  const addAuthentication = useCallback(() => {
    let userModelNode = store.findModelNodeByName("User");

    if (userModelNode) {
      const existingNames = userModelNode.data.properties.map((p) => p.name);
      DEFAULT_AUTH_PROPERTIES.forEach((prop) => {
        if (!existingNames.includes(prop.name)) {
          store.addModelProperty(userModelNode!.id, prop, false);
        }
      });
      onSuccess("Authentication", "Auth properties added to User model");
    } else {
      userModelNode = store.addModelNode("User");
      DEFAULT_AUTH_PROPERTIES.forEach((prop) => {
        store.addModelProperty(userModelNode!.id, prop, false);
      });
      onSuccess("Authentication", "User model created with auth properties");
    }
  }, [store, onSuccess]);

  const addRoleModel = useCallback(() => {
    let roleModelNode = store.findModelNodeByName("Role");

    if (roleModelNode) {
      const existingNames = roleModelNode.data.properties.map((p) => p.name);
      DEFAULT_ROLE_PROPERTIES.forEach((prop) => {
        if (!existingNames.includes(prop.name)) {
          store.addModelProperty(roleModelNode!.id, prop, false);
        }
      });
      onSuccess("RBAC", "Role properties added to Role model");
    } else {
      roleModelNode = store.addModelNode("Role");
      DEFAULT_ROLE_PROPERTIES.forEach((prop) => {
        store.addModelProperty(roleModelNode!.id, prop, false);
      });
      onSuccess("RBAC", "Role model created with RBAC properties");
    }

    // Add roleId reference to User model
    const userModelNode = store.findModelNodeByName("User");
    if (userModelNode) {
      const existingPropertyNames = userModelNode.data.properties.map((p) => p.name);
      const roleIdProperty: ModelPropertyInterface = {
        name: "role",
        type: ModelPropertyType.Reference,
        reference: "Role",
        required: false,
      };

      if (!existingPropertyNames.includes("roleId")) {
        store.addModelProperty(userModelNode.id, roleIdProperty, true);
        onSuccess("RBAC", "roleId reference added to User model");
      }
    }
  }, [store, onSuccess]);

  // ==================== STATE MANAGEMENT ====================

  const setNodesFromModels = useCallback(
    (models: ModelInterface[]) => {
      store.clearModelNodes();
      if (models && models.length > 0) {
        store.bulkAddModelNodes(models);
      }
    },
    [store]
  );

  const clearNodes = useCallback(() => {
    store.clearModelNodes();
  }, [store]);

  const getModelsFromNodes = useCallback(() => {
    return store.getModelsFromModelNodes();
  }, [store]);

  // ==================== EDGE OPERATIONS ====================

  const addEdge = useCallback(
    (from: string, to: string, property?: string) => {
      if (!from || !to) {
        onError("Invalid connection", `Invalid From ${from} TO ${to}`);
        return;
      }
      store.addEdge(from, to, property);
    },
    [store, onError]
  );

  const removeEdge = useCallback(
    (id: string) => {
      store.removeEdge(id);
      return id;
    },
    [store]
  );

  const alignNodes = useCallback(() => {
    const models = store.getModelsFromModelNodes();
    // Pass empty array for existingNodes to force full realignment from scratch
    const newPositions = calculateNewNodePositions(models, []);

    // Apply new positions
    models.forEach((model) => {
      if (newPositions[model.name]) {
        const node = store.findModelNodeByName(model.name);
        if (node) {
          // We need to update the node position in the store
          // Since there isn't a direct "updatePosition" method exposed in the context interface yet,
          // we can use a store method if available, or we might need to add one.
          // Checking store methods... we have bulkAddModelNodes which overwrites if we clear first,
          // but we want to preserve other state.
          // Let's check if we can update the node directly.
          // Actually, calculateNewNodePositions returns positions.
          // We can use a new store method or just update the node object if we had access.
          // Since we are inside the provider, we have access to 'store'.
          // Let's use a bulk update approach or iterate.

          // Ideally we should add a 'updateNodePosition' to the store, but for now let's see if we can use what we have.
          // We can use `store.modelNodes` and update them.
          // But we need to trigger a re-render.
          // Let's use `bulkAddNodes` logic but without clearing everything if possible, 
          // OR just use `setNodesFromModels` which clears and re-adds.
          // Re-adding might lose some transient state (selection etc) but for alignment it's acceptable.
          // Let's update the models with new positions and call bulkAddNodes.

          model.position = newPositions[model.name];
        }
      }
    });

    store.clearModelNodes();
    store.bulkAddModelNodes(models);
    onSuccess("Layout", "Nodes aligned successfully");
  }, [store, onSuccess]);

  // ==================== CONTEXT VALUE ====================

  const value: NodeContextValue = {
    modelNodes: modelNodes,
    edges,
    onNodesChange,
    onEdgesChange,
    addNode,
    removeNode,
    deleteModel,
    addEdge,
    removeEdge,
    onConnect,
    bulkAddNodes,
    addAuthentication,
    addRoleModel,
    setNodesFromModels,
    clearNodes,
    getModelsFromNodes,
    alignNodes,
  };

  return <NodeContext.Provider value={value}>{children}</NodeContext.Provider>;
};

export const useNodes = () => {
  const context = useContext(NodeContext);
  if (!context) {
    throw new Error("useNodes must be used within NodeContextProvider");
  }
  return context;
};
